import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate message length
    const messageText = formData.get('message') as string;
    if (messageText && messageText.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6e40j2ff40lgbk6dmkg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
        setMessage('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 500) {
      setMessage(value);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Contact Form */}
          <div className="bg-slate-50 rounded-lg p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
            <form id="tmi-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition-all"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition-all"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition-all"
                  placeholder="+27 10 634 2114"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition-all"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-slate-700 mb-2">
                  Service Interest *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition-all cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Mining Services">Mining Services</option>
                  <option value="Logistics Services">Logistics Services</option>
                  <option value="Both Mining and Logistics">Both Mining and Logistics</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  Message * <span className="text-slate-500 font-normal">({message.length}/500)</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={message}
                  onChange={handleMessageChange}
                  maxLength={500}
                  className="w-full px-4 py-3 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-[#8B1538] focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Tell us about your project requirements..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#8B1538] hover:bg-[#6B0F2A] text-white font-semibold py-4 px-6 rounded-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-[#8B1538]/10 border border-[#8B1538]/30 text-[#8B1538] px-4 py-3 rounded-md text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! Your message has been sent successfully. We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Sorry, there was an error sending your message. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Contact Information</h2>
              <p className="text-slate-600 mb-8">
                Reach out to us through any of the following channels. Our team is ready to assist you with your mining and logistics needs.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-lg">
                <div className="w-12 h-12 bg-[#8B1538] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                  <p className="text-slate-600">010 634 2114</p>
                  <p className="text-sm text-slate-500 mt-1">Rustenburg: 24/7 Operations</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-lg">
                <div className="w-12 h-12 bg-[#8B1538] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                  <p className="text-slate-600">info@tlominc.co.za</p>
                  <p className="text-sm text-slate-500 mt-1">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-lg">
                <div className="w-12 h-12 bg-[#8B1538] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Midrand Office</h3>
                  <p className="text-slate-600">Unit 1, Tuscan Gardens Office Park</p>
                  <p className="text-slate-600">Noordwyk, Midrand, 1685</p>
                  <p className="text-sm text-slate-500 mt-1">South Africa</p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-6 rounded-lg">
                <div className="w-12 h-12 bg-[#8B1538] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Operating Hours</h3>
                  <div className="space-y-2">
                    <div>
                      <p className="text-slate-900 font-medium text-sm">Midrand Office:</p>
                      <p className="text-slate-600">Monday - Friday: 8:00 AM - 4:30 PM</p>
                      <p className="text-slate-600">Saturday & Sunday: Closed</p>
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-slate-900 font-medium text-sm">Rustenburg Operations:</p>
                      <p className="text-slate-600">Monday - Sunday: 24 Hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
